import { useMemo } from 'react'
import { useZionFormContext } from '@fs/zion-form'
import { defaultGenderLabels } from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'

/**
 * @typedef {Object} SexFieldValidationResult
 * @property {(() => string)|undefined} warningValidate - Function that returns a warning message when the field value is non-standard, or undefined if no warning should be shown
 * @property {string|undefined} validMessage - Success message string when the field value is a standard sex value, or undefined if not valid
 */

/**
 * Custom hook that provides validation logic for sex/gender field inputs.
 * Returns validation functions and messages based on whether the selected value
 * matches standardized gender options from defaultGenderLabels.
 *
 * Validation states:
 * - No value: No validation messages shown
 * - Non-standard value: Warning message ("No Standard Selected")
 * - Standard value: Success message ("Standard Sex")
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.fieldName - The name of the form field to validate
 *
 * @returns {SexFieldValidationResult} Validation configuration object
 */
export const useSexFieldValidation = ({ fieldName }) => {
  const { getValues } = useZionFormContext()
  const currentValue = getValues(fieldName)
  const [t] = useTranslation()

  return useMemo(() => {
    // If there is no value, we don't want to show any validation messages (neither warning nor valid)
    if (!currentValue) {
      return {
        warningValidate: undefined,
        validMessage: undefined,
      }
    }

    // If there is a value but it doesn't match any of the gender keys, show a warning message that the selected value is not a standardized sex
    if (!currentValue?.key || !defaultGenderLabels[currentValue.key]) {
      return {
        warningValidate: () => t('standardizedSexField.validation.warning', 'No Standard Selected'),
        validMessage: undefined,
      }
    }

    // If there is a value and it matches one of the gender keys, show a valid message that the selected value is valid
    return {
      warningValidate: undefined,
      validMessage: t('standardizedSexField.validation.success', 'Standard Sex'),
    }
  }, [currentValue, t])
}
